import { createNativeNode } from "../native/document";
import type { Point, SceneNode } from "../scene/model";
import type { NativeSession } from "../native/session";
import { ImageStore, readImageFile } from "../render/image-store";
/** Decode without touching live state. Originals remain encoded at full quality. */
async function prepareImage(
  file: File,
  name: string | undefined,
  images: ImageStore,
  center: Point,
  pageSize: { width: number; height: number },
) {
  let node: SceneNode;
  let source = "";
  if (/^divider-vector-\d+\.svg$/i.test(file.name)) {
    const xml = new DOMParser().parseFromString(
        await file.text(),
        "image/svg+xml",
      ),
      paths = xml.querySelectorAll("path"),
      d = paths[0]?.getAttribute("d") || "";
    if (
      xml.querySelector("parsererror") ||
      paths.length !== 1 ||
      d.length > 200000 ||
      !/^[\s\d.,+eEMmLlHhVvCcSsQqTtAaZz-]+$/.test(d)
    )
      throw new Error("Повреждён векторный разделитель.");
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg"),
      path = document.createElementNS(
        svg.namespaceURI,
        "path",
      ) as SVGPathElement;
    svg.style.cssText = "position:fixed;visibility:hidden;pointer-events:none";
    path.setAttribute("d", d);
    svg.append(path);
    document.body.append(svg);
    const box = path.getBBox();
    svg.remove();
    if (!box.width || !box.height)
      throw new Error("Пустой векторный разделитель.");
    const scale = Math.min(1, (pageSize.width * 0.8) / box.width);
    node = createNativeNode(
      crypto.randomUUID(),
      "rectangle",
      center.x,
      center.y,
      box.width * scale,
      box.height * scale,
    );
    node.kind = "path";
    node.geometry.svgPath = {
      d,
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
    };
    node.appearance = { fill: "#333333", fillEnabled: true, opacity: 1 };
  } else {
    source = await readImageFile(file);
    await images.prepare([{ id: "decode", kind: "image", source }]);
    const image = images.get(source)!,
      scale = Math.min(
        1,
        pageSize.width / image.naturalWidth,
        pageSize.height / image.naturalHeight,
      );
    node = createNativeNode(
      crypto.randomUUID(),
      "image",
      center.x,
      center.y,
      Math.max(1, image.naturalWidth * scale),
      Math.max(1, image.naturalHeight * scale),
    );
  }
  node.layer.name =
    name ||
    file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ") ||
    "Изображение";
  const asset = source
    ? { id: crypto.randomUUID(), kind: "image" as const, source }
    : null;
  if (asset) node.media.image = asset.id;
  return { node, asset };
}
/** Batch import is one history step; a failed/stale decode never inserts a partial batch. */
export async function importNativeImages(
  entries: readonly { file: File; name?: string | undefined }[],
  session: NativeSession,
  images: ImageStore,
  center: Point,
  valid: () => boolean,
) {
  if (!entries.length) return;
  const generation = session.generation,
    revision = session.revision,
    size = { ...session.page.size },
    prepared: Awaited<ReturnType<typeof prepareImage>>[] = [];
  const current = () => {
    if (
      !valid() ||
      generation !== session.generation ||
      revision !== session.revision ||
      session.gesturing
    )
      throw new Error(
        "Добавление отменено: документ изменился. Перенесите изображения ещё раз.",
      );
  };
  for (const [index, entry] of entries.entries()) {
    current();
    prepared.push(
      await prepareImage(
        entry.file,
        entry.name,
        images,
        { x: center.x + index * 18, y: center.y + index * 18 },
        size,
      ),
    );
  }
  current();
  session.document = {
    ...session.document,
    assets: [
      ...session.document.assets,
      ...prepared.flatMap(({ asset }) => (asset ? [asset] : [])),
    ],
  };
  session.replaceNodes([
    ...session.page.nodes,
    ...prepared.map(({ node }) => node),
  ]);
  session.select(prepared.map(({ node }) => node.id));
  session.finish();
}
export function importNativeImage(
  file: File,
  name: string | undefined,
  session: NativeSession,
  images: ImageStore,
  center: Point,
  valid: () => boolean,
) {
  return importNativeImages([{ file, name }], session, images, center, valid);
}
